import React, { useEffect, useRef, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import LinearProgress from "@mui/material/LinearProgress";
import TablePagination from "@mui/material/TablePagination";
import Typography from "@mui/material/Typography";
import UploadIcon from "@mui/icons-material/Upload";
import { useDispatch, useSelector } from "react-redux";
import { BlogBreadcrumbs } from "../components/BlogBreadcrumbs";
import { MediaCard } from "../components/MediaCard";
import { useConfirm } from "../../../common/confirmDialog/useConfirm";
import { fetchMedia, uploadMedia, deleteMedia } from "../mediaSlice";

export default function MediaLibrary() {
  const dispatch = useDispatch();
  const { media, total, page, loading, error, uploading } = useSelector((state) => state.media);
  const [deletingId, setDeletingId] = useState(null);
  const [dragOver, setDragOver]     = useState(false);
  const confirm = useConfirm();
  const fileRef = useRef(null);
  const rowsPerPage = 24;

  useEffect(() => { dispatch(fetchMedia({ page: 1, limit: rowsPerPage })); }, [dispatch]);

  const upload = (file) => {
    const formData = new FormData();
    formData.append("file", file);
    dispatch(uploadMedia(formData));
  };

  const handleFiles = (files) => {
    Array.from(files).forEach(upload);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleDelete = async (id) => {
    const ok = await confirm({ title: "Delete Media", description: "Are you sure you want to delete this file?", confirmText: "Delete", destructive: true });
    if (!ok) return;
    setDeletingId(id);
    await dispatch(deleteMedia(id));
    setDeletingId(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <BlogBreadcrumbs items={[{ label: "Blog", href: "/blog" }, { label: "Media Library" }]} />
      <Typography variant="h5" sx={{ fontWeight: 700, color: "#362B24", fontFamily: "Heebo", mb: 2 }}>Media Library</Typography>

      <Box
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        sx={{
          border: `2px dashed ${dragOver ? "#065F46" : "#EBEBEB"}`,
          borderRadius: "24px",
          p: 4,
          mb: 3,
          textAlign: "center",
          cursor: "pointer",
          bgcolor: dragOver ? "rgba(6,95,70,0.04)" : "transparent",
          transition: "all 0.2s",
        }}
      >
        <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => handleFiles(e.target.files)} />
        <UploadIcon sx={{ fontSize: 40, color: dragOver ? "#065F46" : "#94a3b8" }} />
        <Typography sx={{ color: "#362B24", opacity: 0.5, fontFamily: "Heebo", mt: 1 }}>
          Click or drag & drop images here
        </Typography>
      </Box>

      {uploading && <LinearProgress sx={{ mb: 2, "& .MuiLinearProgress-bar": { bgcolor: "#065F46" }, bgcolor: "rgba(6,95,70,0.12)" }} />}
      {loading && !uploading && <CircularProgress size={24} sx={{ mb: 2, color: "#065F46" }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={2.5}>
        {media.map((item) => (
          <Grid item key={item._id} xs={6} sm={4} md={3} lg={3}>
            <MediaCard
              media={item}
              onDelete={handleDelete}
              isDeleting={deletingId === item._id}
            />
          </Grid>
        ))}
        {!loading && media.length === 0 && (
          <Grid item xs={12}>
            <Typography sx={{ color: "#362B24", opacity: 0.5, fontFamily: "Heebo", textAlign: "center" }}>
              No media uploaded yet.
            </Typography>
          </Grid>
        )}
      </Grid>

      <TablePagination
        component="div"
        count={total}
        page={page - 1}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[24]}
        onPageChange={(_, pg) => dispatch(fetchMedia({ page: pg + 1, limit: rowsPerPage }))}
      />

    </Box>
  );
}
